import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { registerSW } from 'virtual:pwa-register'
import { App } from './App'
import type { Fix } from './lib/location'
import './styles/fonts.css'
import './styles/tokens.css'
import './styles/glass.css'
import './styles/app.css'

const host = document.getElementById('root')
if (!host) throw new Error('#root is missing from index.html')

createRoot(host).render(
  <StrictMode>
    <App />
  </StrictMode>,
)

// autoUpdate: take the new worker as soon as it's ready. This is a single-page
// app with no unsaved local state, so there's nothing to lose by refreshing.
registerSW({ immediate: true })

// Dev-only handle for driving the app from the console without physically
// travelling to a Best Buy:
//   __nudge.simulate(37.7684, -122.4128)
// Stripped from production builds.
if (import.meta.env.DEV) {
  void (async () => {
    const { setLocationProvider } = await import('./lib/location')
    const { useStore } = await import('./lib/store')

    let listener: ((fix: Fix) => void) | null = null
    let current: Fix = { lat: 37.7749, lon: -122.4194, accuracyM: 12, at: Date.now() }

    setLocationProvider({
      canRunInBackground: false,
      isSupported: () => true,
      getPermission: async () => 'granted',
      getCurrent: async () => current,
      watch: (onFix) => {
        listener = onFix
        onFix(current)
        return () => {
          listener = null
        }
      },
    })

    Object.assign(window, {
      __nudge: {
        simulate(lat: number, lon: number) {
          current = { lat, lon, accuracyM: 12, at: Date.now() }
          // Bypass the client-side rate limit so a simulated jump reports now.
          useStore.setState({ fix: current })
          listener?.(current)
          return useStore.getState().pushFix(current, true)
        },
        store: useStore,
      },
    })
    console.info('[nudge] dev location seam ready — __nudge.simulate(lat, lon)')
  })()
}
